/* eslint-disable @typescript-eslint/no-explicit-any */
import { Button, TextLink, useToast } from '@bfe/moly';
import { IconTax } from '@bfe/moly-icons';
import React, { FC, lazy, Suspense, useMemo, useState } from 'react';

import AccountBasicItem from '@/components/AccountBasicItem';
import useTaxJurisdiction, { Resident } from '@/hooks/useTaxJurisdiction';
import { useI18nContext } from '@/i18n/I18n';

const AssetOrderModal = lazy(() => import('./AssetOrderModal'));

const SubText = ({
  resident,
  triggerAssetOrder,
}: {
  resident: string;
  triggerAssetOrder: () => void;
}) => {
  const [$t] = useI18nContext();

  const desc = useMemo(() => {
    if (resident === Resident['P-AUT']) {
      return 'TaxJurisdictionDesc_P-AUT';
    }
    if (resident === Resident['AUT-PROCESSING']) {
      return 'TaxJurisdictionDesc_AUT-PROCESSING';
    }
    if (resident === Resident['AUT-OUT']) {
      return 'TaxJurisdictionDesc_AUT-TIMEOUT';
    }
    return 'TaxJurisdictionDescNew';
  }, [resident]);

  const linkText = useMemo(() => {
    if (resident === Resident['AUT-PROCESSING']) {
      return {
        text: 'TaxJurisdictionDesc_AUT-PROCESSING_btn',
        link: '/user/assets/home/fiat?tab=pending-assets',
      };
    }
    if (resident === Resident.AUT || resident === Resident['AUT-NEW']) {
      return {
        text: 'TaxJurisdictionAssetOrderBtn',
        link: '',
        action: triggerAssetOrder,
      };
    }
    if (resident === Resident['AUT-OUT']) {
      return {
        text: 'TaxJurisdictionDesc_AUT-TIMEOUT_btn',
        link: '/help-center/article/FAQ-Tax-Withholding-in-Austria',
      };
    }
    return {
      text: '',
      link: '',
    };
  }, [resident]);

  const linkProps = linkText?.link
    ? {
        href: linkText.link,
      }
    : linkText?.action
    ? {
        onClick: triggerAssetOrder,
      }
    : {};

  return (
    <div className="tax-jurisdiction-desc">
      {$t(desc)}
      {linkText && (
        <TextLink variant="primary" size="small" closeIcon {...linkProps}>
          {$t(linkText.text)}
        </TextLink>
      )}
    </div>
  );
};

const TaxJurisdiction: FC = () => {
  const [$t] = useI18nContext();
  const { toast, EToastType } = useToast();

  const { resident, forceUpdateTaxResident } = useTaxJurisdiction();

  const [showAssetOrderModal, setShowAssetOrderModal] = useState(false);
  const [loadAssetOrderModal, setLoadAssetOrderModal] = useState(false);

  const status = useMemo(() => {
    return [
      Resident.AUT,
      Resident['AUT-NEW'],
      Resident['AUT-PROCESSING'],
    ].includes(resident);
  }, [resident]);

  const statusText = useMemo(() => {
    if (resident === Resident['AUT-OUT']) {
      return `taxJurisdictionResident_NOT-AUT`;
    }
    if (
      [Resident.AUT, Resident['AUT-NEW'], Resident['AUT-PROCESSING']].includes(
        resident,
      )
    ) {
      return `taxJurisdictionResident_AUT`;
    }
    if (resident === 'P-AUT') {
      return `taxJurisdictionResident_P-AUT`;
    }
    return 'taxJurisdictionResident_NOT-AUT';
  }, [resident]);

  const buttonText = useMemo(() => {
    // if (resident === Resident.default) {
    //   return 'Set up';
    // }
    if (resident === Resident['P-AUT']) {
      return 'taxJurisdictionResidentButton_P-AUT';
    }
    return 'Change';
  }, [resident]);

  const openAMP = () => {
    window?.BybitUniFrame?.ready(async ({ AMPDialog }: { AMPDialog: any }) => {
      // 初始化组件
      await AMPDialog?.init?.();

      AMPDialog?.setProperty?.(AMPDialog?.properties?.VISIBLE, true)
        ?.setProperty?.(AMPDialog?.properties?.THEME, 'theme-light')
        ?.setProperty?.(AMPDialog?.properties?.FROM, 'us_AUT')
        ?.setProperty?.(AMPDialog?.properties?.PRODUCT_ID, '100005');

      // 监听取消
      AMPDialog?.subscribeEvent(AMPDialog?.events?.HIDE_MODAL, () => {
        AMPDialog?.dealloc?.(); // 问卷使用场景不多，所以关闭就直接销毁
      });

      // 监听完成
      await AMPDialog?.subscribeEvent(AMPDialog?.events?.SUCCESS, () => {
        AMPDialog?.dealloc?.(); // 问卷使用场景不多，所以完成后就直接销毁
        forceUpdateTaxResident();
      });
    });
  };

  const handleSetting = () => {
    openAMP();
  };

  const handleChange = () => {
    if (resident === Resident['AUT-PROCESSING']) {
      toast({
        title: $t('AUT-PROCESSING_ChangeClickTip'),
        iconType: EToastType.WARN,
      });
      return;
    }
    if (resident === Resident['AUT-OUT']) {
      toast({
        title: $t('AUT-OUT_ChangeClickTip'),
        iconType: EToastType.WARN,
      });
      return;
    }
    openAMP();
  };

  const triggerAssetOrder = () => {
    setShowAssetOrderModal(true);
    setLoadAssetOrderModal(true);
  };

  return (
    <>
      <AccountBasicItem
        icon="tax"
        molyIcon={<IconTax />}
        text={$t('TaxJurisdiction')}
        subText={
          <SubText resident={resident} triggerAssetOrder={triggerAssetOrder} />
        }
        status={resident !== Resident['P-AUT']}
        statusNode={$t(statusText)}
        operation={
          <Button
            size="small"
            data-radius="small"
            variant={buttonText === 'Change' ? 'secondary' : 'primary'}
            onClick={() => {
              if (status || resident === Resident['AUT-OUT']) {
                handleChange();
              } else {
                handleSetting();
              }
            }}
          >
            {$t(buttonText)}
          </Button>
        }
      />
      <Suspense>
        {loadAssetOrderModal && (
          <AssetOrderModal
            open={showAssetOrderModal}
            onSuccess={() => {
              setShowAssetOrderModal(false);
            }}
            onCancel={() => {
              setShowAssetOrderModal(false);
            }}
          />
        )}
      </Suspense>
    </>
  );
};
export default TaxJurisdiction;
